import {
  answerCard,
  microsoft365WriteCard,
  microsoft365WriteOutcome,
  turnFailureSchema,
  type AnswerCard,
  type ApprovalModeDto,
  type ApprovalOptionDto,
  type ConversationEvent,
  type Microsoft365WriteCard,
  type Microsoft365WriteOutcome,
  type QuotaNoticeDto,
  type SentArgumentsDto,
  type TurnFailureDto,
} from '@dbrain/shared';

/**
 * Client view of a conversation built from the event stream (ADR-0004).
 * Durable events are applied once, in seq order. Deltas build drafts that
 * the consolidated assistant message replaces, so a live view and a reload
 * end up identical (QW-BUG-003). Cards, the task list, helpers and
 * background tasks are rebuilt from the same events (Phase 9). A rewind
 * removes its range from the view as it does from the model's history;
 * compactions, branches and saved memories appear where they happened
 * (Phase 10).
 */
export interface Attachment {
  fileId: string;
  name: string;
  size: number;
  mediaType: string;
}

/** DOC-004: a passage an answer cites, with the sentence of it that supports the answer. */
export interface CitationView {
  n: number;
  documentId: string;
  documentName: string;
  kind: 'page' | 'sheet' | 'slide' | 'section';
  number: number;
  label: string | null;
  rowFrom: number | null;
  rowTo: number | null;
  quote: string;
}

export type NoticeCode = 'failed' | 'cancelled' | 'rejected' | 'limit' | 'interrupted' | 'run_interrupted' | 'run_resumed';

export interface HelperStep {
  callId: string;
  title: string;
  status: 'running' | 'done' | 'failed';
}

/** A sub-agent (D-02, QW-806, QW-809): its steps stay out of the main flow, shown under its card. */
export interface HelperInfo {
  subRunId: string;
  description: string;
  background: boolean;
  status: 'running' | 'completed' | 'failed' | 'cancelled' | 'interrupted';
  stopReason: string | null;
  usage: { inputTokens: number; outputTokens: number; costMicros: number; modelCalls: number; toolCalls?: number; durationMs?: number } | null;
  steps: HelperStep[];
  lastText: string | null;
}

export type ChatItem =
  | { kind: 'user'; seq: number; messageId: string; text: string; attachments: Attachment[]; createdAt: string }
  | {
      kind: 'assistant';
      seq: number;
      messageId: string;
      runId: string;
      text: string;
      reasoning: string | null;
      /** D-77: with reasoning, how long the visible text took; the rest of the step was reasoning */
      writingMs: number | null;
      citations: CitationView[];
      /** D-197 (Phase 15e): Microsoft 365 answer cards, each at its offset into `text` */
      cards: AnswerCard[];
      /** when the model call that wrote it ended, and when it started (the run's previous event) */
      createdAt: string;
      startedAt: string | null;
    }
  | {
      kind: 'notice';
      seq: number;
      runId: string | null;
      code: NoticeCode;
      errorCode: string | null;
      limit: { kind: string; used: number; max: number } | null;
      /** SA-023 (D-97): the token budget that ran out during the turn */
      quota?: QuotaNoticeDto | null;
      /** 16b.1: why a `no_visible_reply` turn got no answer (none before v0.16b.1) */
      failure?: TurnFailureDto | null;
    }
  | {
      kind: 'tool';
      seq: number;
      callId: string;
      runId: string;
      stepId: string | null;
      tool: string;
      title: string;
      summary: string;
      details: string | null;
      status: 'running' | 'done' | 'failed';
      errorCode: string | null;
      result: string | null;
      helper: HelperInfo | null;
      /** the call's arguments as sent (diff view of an edit) */
      arguments: string;
      /** QW-406: how long the call took */
      durationMs: number | null;
      /** QW-BUG-056: ran without asking because this skill granted it */
      grantedBy: string | null;
      /** D-179: a connection's tool that ran without asking, and why */
      allowedBy: 'trusted_read' | 'tool_policy' | 'rule' | 'full_access' | null;
      /** Phase 15e: a built-in tool's outcome in plain words ("Gelen kutusu okundu · 24 mail"); the steps line shows it */
      label: string | null;
      startedAt: string;
    }
  | { kind: 'artifact'; seq: number; runId: string | null; artifactId: string; name: string; artifactKind: string; mediaType: string; size: number; version: number }
  | { kind: 'approval'; seq: number; approvalId: string }
  | { kind: 'plan'; seq: number; runId: string | null; plan: string; mode: string; edited: boolean }
  | { kind: 'notification'; seq: number; label: string; status: string }
  | { kind: 'compaction'; seq: number; reason: string; throughSeq: number; summary: string; tokensBefore: number | null; tokensAfter: number | null }
  | { kind: 'memory'; seq: number; memoryId: string; content: string }
  | { kind: 'forked'; seq: number; fromConversationId: string; title: string | null }
  | {
      kind: 'skill';
      seq: number;
      runId: string;
      callId: string | null;
      name: string;
      level: string;
      version: number | null;
      part: number;
      parts: number;
      invokedBy: 'model' | 'user';
      grants: { commands: string[]; ownScripts: boolean };
      reused: boolean;
    }
  | { kind: 'capabilities'; seq: number; added: { kind: string; name: string }[]; removed: { kind: string; name: string }[] }
  /** D-92: the turn waits for documents still being read before the model starts */
  | { kind: 'docwait'; seq: number; runId: string; documents: string[]; status: 'running' | 'done' | 'failed'; waitedMs: number | null; startedAt: string }
  /** D-99: a server the agent started listens on a port; the line offers "Önizlemede aç" (the panel never opens on its own) */
  | { kind: 'preview'; seq: number; runId: string | null; port: number };

/** DOC-013: a web page the model read in this conversation. */
export interface SourceView {
  seq: number;
  runId: string | null;
  callId: string | null;
  url: string;
  title: string | null;
  accessedAt: string;
}

/** A turn: when it started and ended, how, and the messages it answered (Phase 13b, the steps line). */
export interface RunView {
  runId: string;
  startedAt: string;
  completedAt: string | null;
  reason: string | null;
  /** why a failed turn failed: "quota_exceeded" reads "Bütçe dolduğu için durdu" (D-97) */
  errorCode?: string | null;
  /** 16b.1: why a `no_visible_reply` turn got no answer: its chip says it and opens to the tries */
  failure?: TurnFailureDto | null;
  inputSeqs: number[];
  /** the time of the run's latest event: where the next model call started */
  lastAt: string;
}

/** QW-301, QW-311: the last request's size and what it was made of. */
export interface ContextUsage {
  promptTokens: number;
  contextWindow: number;
  exact: boolean;
  categories: { key: string; tokens: number }[];
  /** QW-1217: each loaded skill's share */
  skills?: { name: string; tokens: number }[];
}

export interface ApprovalView {
  id: string;
  runId: string;
  kind: 'tool' | 'plan' | 'question';
  callId: string;
  tool: string;
  title: string;
  summary: string;
  details: string | null;
  /** D-178: what a connection's tool call sends */
  sent: SentArgumentsDto | null;
  /** D-180: a tool that always asks */
  locked: boolean;
  effects: string[];
  options: ApprovalOptionDto[];
  plan: string | null;
  questions: { question: string; header?: string; multiSelect?: boolean; options: { label: string; description?: string }[] }[];
  expiresAt: string | null;
  subagent: { runId: string; description: string } | null;
  createdAt: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'cancelled';
  option: string | null;
  /**
   * Microsoft 365 part 3 (MsSohbetOnay, MsToplantiOnay): a mail or meeting card (the canonical payload text, its hash
   * and the view text), sent only through its own route; null for every other card or one this view cannot read
   */
  microsoft365: Microsoft365WriteCard | null;
  /** how that card ended (`approval.resolved.microsoft365`): sent, created, failed, unknown, expired, cancelled, stopped */
  microsoft365Outcome: Microsoft365WriteOutcome | null;
}

export interface TodoView {
  id: string;
  content: string;
  status: 'pending' | 'in_progress' | 'completed';
  blockedBy?: string[];
}

export interface TaskView {
  id: string;
  kind: 'shell' | 'subagent';
  label: string;
  status: string;
  summary: string | null;
}

export interface ChatState {
  lastSeq: number;
  items: ChatItem[];
  drafts: Record<string, string>;
  reasoningDrafts: Record<string, string>;
  finalized: string[];
  running: boolean;
  activeRunId: string | null;
  /** live tool output by call id (not replayed after a reload; code workspaces only show it) */
  toolOutput: Record<string, string>;
  approvals: Record<string, ApprovalView>;
  todos: TodoView[];
  mode: ApprovalModeDto | null;
  tasks: Record<string, TaskView>;
  /** D-06: the last port that started listening; nonce changes on every detection */
  preview: { port: number; nonce: number } | null;
  /** D-06: grows when the agent changes files (preview refresh) */
  filesVersion: number;
  /** D-05, MM-021: the model call is being retried, the next try after delayMs */
  retry: { attempt: number; max: number; delayMs: number } | null;
  /** user messages that reached a turn (others wait in the queue, QW-102) */
  delivered: number[];
  /** sub-agent run → the parent's agent call */
  subRuns: Record<string, string>;
  /** the task list over time, so a rewind can go back to the one before it */
  todoHistory: { seq: number; items: TodoView[] }[];
  /** messages up to here are in the summary; rewinding into them is not possible (ADR-0010) */
  compactedThrough: number;
  /** a compaction is running (the answer waits for it) */
  compacting: string | null;
  context: ContextUsage | null;
  /** grows when the title, pin, group or model changes elsewhere (QW-203) */
  metaVersion: number;
  /** seqs where the workspace was saved before a change (rewind with files) */
  snapshots: number[];
  /** MM-008, QW-611: tokens of each finished turn, by run */
  turnUsage: Record<string, TurnUsage>;
  /** DOC-013: pages read in the conversation, first read first */
  sources: SourceView[];
  /** Phase 12: per turn, the documents searched or read and how many passages the model got */
  docSearches: Record<string, { documents: string[]; passages: number }>;
  /** Phase 13b: every turn's timing, for the steps line */
  runs: Record<string, RunView>;
  /** Phase 15e: where a Microsoft 365 tool gave the model mails, events or attachments (`microsoft.items` seqs) */
  microsoftItems: number[];
}

export interface TurnUsage {
  promptTokens: number;
  inputTokens: number;
  outputTokens: number;
  contextWindow: number;
  modelCalls: number;
  model?: string;
}

export const initialChatState: ChatState = {
  lastSeq: 0,
  items: [],
  drafts: {},
  reasoningDrafts: {},
  finalized: [],
  running: false,
  activeRunId: null,
  toolOutput: {},
  approvals: {},
  todos: [],
  mode: null,
  tasks: {},
  preview: null,
  filesVersion: 0,
  retry: null,
  delivered: [],
  subRuns: {},
  todoHistory: [],
  compactedThrough: 0,
  compacting: null,
  context: null,
  metaVersion: 0,
  snapshots: [],
  turnUsage: {},
  sources: [],
  docSearches: {},
  runs: {},
  microsoftItems: [],
};

const MAX_TOOL_OUTPUT = 64 * 1024;
const MAX_RESULT = 4_000;
const str = (value: unknown): string => (typeof value === 'string' ? value : '');

/** D-197: the message's answer cards; one this view cannot read is left out (its text still shows). */
function cardsOf(value: unknown): AnswerCard[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((card) => {
    const parsed = answerCard.safeParse(card);
    return parsed.success ? [parsed.data] : [];
  });
}

/** A Microsoft 365 write card or its end, when it reads as the contract says; anything else is none. */
function writeCardOf(value: unknown): Microsoft365WriteCard | null {
  const parsed = microsoft365WriteCard.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function writeOutcomeOf(value: unknown): Microsoft365WriteOutcome | null {
  const parsed = microsoft365WriteOutcome.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** 16b.1: why a turn got no answer, as `turn.completed` carries it; anything else is none. */
function failureOf(value: unknown): TurnFailureDto | null {
  const parsed = turnFailureSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** A budget that ran out (SA-023), as `turn.completed` carries it; anything else is no budget. */
function quotaOf(value: unknown): QuotaNoticeDto | null {
  if (!value || typeof value !== 'object') return null;
  const q = value as Record<string, unknown>;
  if ((q.scope !== 'user_daily' && q.scope !== 'tenant_monthly') || typeof q.resetsAt !== 'string') return null;
  return {
    scope: q.scope,
    resetsAt: q.resetsAt,
    ...(typeof q.used === 'number' && typeof q.limit === 'number' ? { used: q.used, limit: q.limit } : {}),
  };
}

function updateTool(items: ChatItem[], callId: string, change: (item: Extract<ChatItem, { kind: 'tool' }>) => Extract<ChatItem, { kind: 'tool' }>): ChatItem[] {
  return items.map((item) => (item.kind === 'tool' && item.callId === callId ? change(item) : item));
}

function updateHelper(items: ChatItem[], callId: string | undefined, change: (helper: HelperInfo) => HelperInfo): ChatItem[] {
  if (!callId) return items;
  return updateTool(items, callId, (item) => (item.helper ? { ...item, helper: change(item.helper) } : item));
}

function applyEphemeral(state: ChatState, event: Extract<ConversationEvent, { kind: 'ephemeral' }>): ChatState {
  const p = event.payload;
  switch (event.type) {
    case 'tool.output': {
      const callId = str(p.callId);
      const output = (state.toolOutput[callId] ?? '') + str(p.text);
      return { ...state, toolOutput: { ...state.toolOutput, [callId]: output.slice(-MAX_TOOL_OUTPUT) } };
    }
    case 'assistant.delta': {
      const messageId = str(p.messageId);
      if (!messageId || state.finalized.includes(messageId)) return state;
      return { ...state, running: true, retry: null, drafts: { ...state.drafts, [messageId]: (state.drafts[messageId] ?? '') + str(p.text) } };
    }
    case 'reasoning.delta': {
      const messageId = str(p.messageId);
      if (!messageId || state.finalized.includes(messageId)) return state;
      return { ...state, retry: null, reasoningDrafts: { ...state.reasoningDrafts, [messageId]: (state.reasoningDrafts[messageId] ?? '') + str(p.text) } };
    }
    case 'model.retry':
      return { ...state, retry: { attempt: Number(p.attempt ?? 1), max: Number(p.max ?? 1), delayMs: Number(p.delayMs ?? 0) } };
    case 'files.changed':
      return { ...state, filesVersion: state.filesVersion + 1 };
    case 'context.usage':
      return {
        ...state,
        context: {
          promptTokens: Number(p.promptTokens ?? 0),
          contextWindow: Number(p.contextWindow ?? 0),
          exact: p.exact === true,
          categories: Array.isArray(p.categories) ? (p.categories as ContextUsage['categories']) : [],
          ...(Array.isArray(p.skills) ? { skills: p.skills as NonNullable<ContextUsage['skills']> } : {}),
        },
      };
    case 'context.compacting':
      return { ...state, compacting: str(p.reason) || 'auto' };
    default:
      return state;
  }
}

export function applyEvent(state: ChatState, event: ConversationEvent): ChatState {
  if (event.kind === 'ephemeral') return applyEphemeral(state, event);
  if (event.seq <= state.lastSeq) return state;
  const next: ChatState = { ...state, lastSeq: event.seq };
  const p = event.payload;
  const runId = str(p.runId);
  const helperCall = state.subRuns[runId];
  const run = state.runs[runId];
  // a step of the run starts where its previous event ended
  if (run && event.type !== 'turn.started') next.runs = { ...state.runs, [runId]: { ...run, lastAt: event.createdAt } };
  switch (event.type) {
    case 'user.message':
      next.items = [
        ...state.items,
        {
          kind: 'user',
          seq: event.seq,
          messageId: str(p.messageId),
          text: str(p.text),
          attachments: Array.isArray(p.attachments) ? (p.attachments as Attachment[]) : [],
          createdAt: event.createdAt,
        },
      ];
      break;
    case 'turn.started': {
      next.running = true;
      next.activeRunId = runId || null;
      const inputSeqs = Array.isArray(p.inputSeqs) ? (p.inputSeqs as number[]) : [];
      next.delivered = [...state.delivered, ...inputSeqs];
      // a resumed run keeps its first start
      if (runId) {
        next.runs = {
          ...state.runs,
          [runId]: run
            ? { ...run, completedAt: null, reason: null, errorCode: null, lastAt: event.createdAt }
            : { runId, startedAt: event.createdAt, completedAt: null, reason: null, errorCode: null, inputSeqs, lastAt: event.createdAt },
        };
      }
      break;
    }
    case 'input.delivered': {
      next.delivered = [...state.delivered, ...(Array.isArray(p.seqs) ? (p.seqs as number[]) : [])];
      const notes = Array.isArray(p.notifications) ? (p.notifications as Record<string, unknown>[]) : [];
      if (notes.length > 0) {
        next.items = [...state.items, ...notes.map((n) => ({ kind: 'notification' as const, seq: event.seq, label: str(n.label), status: str(n.status) }))];
      }
      break;
    }
    case 'tool.started':
      if (helperCall) {
        next.items = updateHelper(state.items, helperCall, (h) => ({ ...h, steps: [...h.steps, { callId: str(p.callId), title: str(p.title), status: 'running' }] }));
        break;
      }
      next.items = [
        ...state.items,
        {
          kind: 'tool',
          seq: event.seq,
          callId: str(p.callId),
          runId,
          stepId: typeof p.stepId === 'string' ? p.stepId : null,
          tool: str(p.tool),
          title: str(p.title),
          summary: str(p.summary),
          details: typeof p.details === 'string' ? p.details : null,
          status: 'running',
          errorCode: null,
          result: null,
          helper: null,
          arguments: str(p.arguments),
          durationMs: null,
          grantedBy: null,
          allowedBy: null,
          label: null,
          startedAt: event.createdAt,
        },
      ];
      break;
    case 'tool.executing':
      if (!helperCall && typeof p.grantedBy === 'string' && p.grantedBy) {
        const grantedBy = p.grantedBy;
        next.items = updateTool(state.items, str(p.callId), (item) => ({ ...item, grantedBy }));
      }
      if (!helperCall && typeof p.allowedBy === 'string' && p.allowedBy) {
        const allowedBy = p.allowedBy as Extract<ChatItem, { kind: 'tool' }>['allowedBy'];
        next.items = updateTool(next.items, str(p.callId), (item) => ({ ...item, allowedBy }));
      }
      break;
    case 'skill.loaded': {
      // a helper's skills stay with the helper (QW-1221)
      if (helperCall) break;
      const grants = (p.grants ?? { commands: [], ownScripts: false }) as { commands: string[]; ownScripts: boolean };
      next.items = [
        ...state.items,
        {
          kind: 'skill',
          seq: event.seq,
          runId,
          callId: typeof p.callId === 'string' ? p.callId : null,
          name: str(p.name),
          level: str(p.level),
          version: typeof p.version === 'number' ? p.version : null,
          part: Number(p.part ?? 1),
          parts: Number(p.parts ?? 1),
          invokedBy: p.invokedBy === 'user' ? 'user' : 'model',
          grants: { commands: Array.isArray(grants.commands) ? grants.commands : [], ownScripts: grants.ownScripts === true },
          reused: p.reused === true,
        },
      ];
      break;
    }
    case 'capabilities.changed':
      next.items = [
        ...state.items,
        {
          kind: 'capabilities',
          seq: event.seq,
          added: Array.isArray(p.added) ? (p.added as { kind: string; name: string }[]) : [],
          removed: Array.isArray(p.removed) ? (p.removed as { kind: string; name: string }[]) : [],
        },
      ];
      break;
    case 'source.added': {
      const url = str(p.url);
      if (url && !state.sources.some((source) => source.url === url)) {
        next.sources = [
          ...state.sources,
          { seq: event.seq, runId: runId || null, callId: typeof p.callId === 'string' ? p.callId : null, url, title: typeof p.title === 'string' ? p.title : null, accessedAt: str(p.accessedAt) || event.createdAt },
        ];
      }
      break;
    }
    case 'tool.completed': {
      const status = p.ok === true ? 'done' : 'failed';
      if (helperCall) {
        next.items = updateHelper(state.items, helperCall, (h) => ({ ...h, steps: h.steps.map((s) => (s.callId === p.callId ? { ...s, status } : s)) }));
        break;
      }
      next.items = updateTool(state.items, str(p.callId), (item) => ({
        ...item,
        status,
        errorCode: typeof p.errorCode === 'string' ? p.errorCode : null,
        // QW-406: the result stays with the call (already masked by the server)
        result: typeof p.result === 'string' ? (item.tool === 'agent' ? p.result : p.result.slice(0, MAX_RESULT)) : item.result,
        durationMs: typeof p.durationMs === 'number' ? p.durationMs : null,
        label: typeof p.label === 'string' && p.label.trim() ? p.label.trim() : null,
      }));
      break;
    }
    case 'microsoft.items':
      // also a helper's: the conversation used Microsoft 365 (the bar's "Microsoft 365 bağlı")
      next.microsoftItems = [...state.microsoftItems, event.seq];
      break;
    case 'subagent.started': {
      const callId = str(p.callId);
      next.subRuns = { ...state.subRuns, [str(p.subRunId)]: callId };
      next.items = updateTool(state.items, callId, (item) => ({
        ...item,
        helper: {
          subRunId: str(p.subRunId),
          description: str(p.description),
          background: p.background === true,
          status: 'running',
          stopReason: null,
          usage: null,
          steps: [],
          lastText: null,
        },
      }));
      break;
    }
    case 'subagent.completed':
      next.items = updateHelper(state.items, state.subRuns[str(p.subRunId)], (h) => ({
        ...h,
        status: (str(p.status) || 'completed') as HelperInfo['status'],
        stopReason: str(p.stopReason) || null,
        usage: (p.usage as HelperInfo['usage']) ?? null,
      }));
      break;
    case 'artifact.created':
      next.items = [
        ...state.items,
        {
          kind: 'artifact',
          seq: event.seq,
          runId: runId || null,
          artifactId: str(p.artifactId),
          name: str(p.name),
          artifactKind: str(p.kind) || 'other',
          mediaType: str(p.mediaType),
          size: Number(p.size ?? 0),
          version: Number(p.version ?? 1),
        },
      ];
      break;
    case 'assistant.message': {
      const messageId = str(p.messageId);
      const { [messageId]: _draft, ...drafts } = state.drafts;
      const { [messageId]: _reasoning, ...reasoningDrafts } = state.reasoningDrafts;
      next.drafts = drafts;
      next.reasoningDrafts = reasoningDrafts;
      next.finalized = [...state.finalized, messageId];
      if (helperCall) {
        next.items = updateHelper(state.items, helperCall, (h) => ({ ...h, lastText: str(p.text) || h.lastText }));
        break;
      }
      next.items = [
        ...state.items,
        {
          kind: 'assistant',
          seq: event.seq,
          messageId,
          runId,
          text: str(p.text),
          reasoning: typeof p.reasoning === 'string' && p.reasoning ? p.reasoning : null,
          writingMs: typeof p.writingMs === 'number' ? p.writingMs : null,
          citations: Array.isArray(p.citations) ? (p.citations as CitationView[]) : [],
          cards: cardsOf(p.cards),
          createdAt: event.createdAt,
          startedAt: run?.lastAt ?? null,
        },
      ];
      break;
    }
    case 'docs.waiting': {
      const documents = Array.isArray(p.documents) ? (p.documents as { name?: string }[]).map((d) => str(d.name)).filter(Boolean) : [];
      // a run another worker took over waits again under the same step
      const open = state.items.findIndex((item) => item.kind === 'docwait' && item.runId === runId && item.status === 'running');
      const item = { kind: 'docwait' as const, seq: event.seq, runId, documents, status: 'running' as const, waitedMs: null, startedAt: event.createdAt };
      next.items = open >= 0 ? state.items.map((x, i) => (i === open ? { ...item, startedAt: (x as { startedAt: string }).startedAt } : x)) : [...state.items, item];
      break;
    }
    case 'docs.ready': {
      const documents = Array.isArray(p.documents) ? (p.documents as { status?: string }[]) : [];
      const ok = documents.every((d) => d.status === 'ready');
      next.items = state.items.map((item) =>
        item.kind === 'docwait' && item.runId === runId && item.status === 'running' ? { ...item, status: ok ? 'done' : 'failed', waitedMs: Number(p.waitedMs ?? 0) } : item,
      );
      break;
    }
    case 'doc.passages': {
      const passages = Array.isArray(p.passages) ? (p.passages as { documentName?: string }[]) : [];
      const before = state.docSearches[runId] ?? { documents: [], passages: 0 };
      const names = passages.map((x) => str(x.documentName)).filter((name) => name && !before.documents.includes(name));
      next.docSearches = { ...state.docSearches, [runId]: { documents: [...before.documents, ...new Set(names)], passages: before.passages + passages.length } };
      break;
    }
    case 'approval.requested': {
      const id = str(p.approvalId);
      next.approvals = {
        ...state.approvals,
        [id]: {
          id,
          runId,
          kind: (str(p.kind) || 'tool') as ApprovalView['kind'],
          callId: str(p.callId),
          tool: str(p.tool),
          title: str(p.title),
          summary: str(p.summary),
          details: typeof p.details === 'string' ? p.details : null,
          sent: p.sent && typeof p.sent === 'object' ? (p.sent as SentArgumentsDto) : null,
          locked: p.locked === true,
          effects: Array.isArray(p.effects) ? (p.effects as string[]) : [],
          options: Array.isArray(p.options) ? (p.options as ApprovalOptionDto[]) : [],
          plan: typeof p.plan === 'string' ? p.plan : null,
          questions: Array.isArray(p.questions) ? (p.questions as ApprovalView['questions']) : [],
          expiresAt: typeof p.expiresAt === 'string' ? p.expiresAt : null,
          subagent: (p.subagent as ApprovalView['subagent']) ?? null,
          createdAt: event.createdAt,
          status: 'pending',
          option: null,
          microsoft365: writeCardOf(p.microsoft365),
          microsoft365Outcome: null,
        },
      };
      next.items = [...state.items, { kind: 'approval', seq: event.seq, approvalId: id }];
      break;
    }
    case 'approval.resolved': {
      const id = str(p.approvalId);
      const current = state.approvals[id];
      if (current) {
        next.approvals = {
          ...state.approvals,
          [id]: {
            ...current,
            status: (str(p.status) || 'approved') as ApprovalView['status'],
            option: str(p.option) || null,
            // part 3: the outcome and the version that went out (the card stays in the flow with it)
            microsoft365Outcome: writeOutcomeOf(p.microsoft365),
          },
        };
      }
      break;
    }
    case 'mode.changed':
      next.mode = (str(p.mode) || 'ask') as ApprovalModeDto;
      break;
    case 'todo.updated':
      if (!helperCall) {
        next.todos = Array.isArray(p.items) ? (p.items as TodoView[]) : [];
        next.todoHistory = [...state.todoHistory, { seq: event.seq, items: next.todos }];
      }
      break;
    case 'plan.approved':
      next.items = [...state.items, { kind: 'plan', seq: event.seq, runId: runId || null, plan: str(p.plan), mode: str(p.mode), edited: p.edited === true }];
      break;
    case 'task.updated': {
      const id = str(p.taskId);
      next.tasks = {
        ...state.tasks,
        [id]: { id, kind: (str(p.kind) || 'shell') as TaskView['kind'], label: str(p.label), status: str(p.status), summary: typeof p.summary === 'string' ? p.summary : (state.tasks[id]?.summary ?? null) },
      };
      break;
    }
    case 'preview.detected':
      next.preview = { port: Number(p.port), nonce: (state.preview?.nonce ?? 0) + 1 };
      next.items = [...state.items, { kind: 'preview', seq: event.seq, runId: runId || null, port: Number(p.port) }];
      break;
    case 'system.note': {
      const code = str(p.code);
      if (code === 'run_interrupted' || code === 'run_resumed') {
        next.items = [...state.items, { kind: 'notice', seq: event.seq, runId: null, code, errorCode: null, limit: null }];
      }
      break;
    }
    case 'conversation.rewound': {
      // QW-106, QW-1306: the range leaves the view as it left the model's history
      const from = Number(p.fromSeq);
      const removedCards = new Set(state.items.filter((item) => item.kind === 'approval' && item.seq >= from).map((item) => (item as { approvalId: string }).approvalId));
      next.items = state.items.filter((item) => item.seq < from);
      next.approvals = Object.fromEntries(Object.entries(state.approvals).filter(([id]) => !removedCards.has(id)));
      next.todoHistory = state.todoHistory.filter((entry) => entry.seq < from);
      next.todos = next.todoHistory.at(-1)?.items ?? [];
      next.delivered = state.delivered.filter((seq) => seq < from);
      next.snapshots = state.snapshots.filter((seq) => seq < from);
      next.sources = state.sources.filter((source) => source.seq < from);
      next.microsoftItems = state.microsoftItems.filter((seq) => seq < from);
      break;
    }
    case 'context.compacted':
      next.compacting = null;
      next.compactedThrough = Math.max(state.compactedThrough, Number(p.throughSeq ?? 0));
      next.items = [
        ...state.items,
        {
          kind: 'compaction',
          seq: event.seq,
          reason: str(p.reason),
          throughSeq: Number(p.throughSeq ?? 0),
          summary: str(p.summary),
          tokensBefore: typeof p.tokensBefore === 'number' ? p.tokensBefore : null,
          tokensAfter: typeof p.tokensAfter === 'number' ? p.tokensAfter : null,
        },
      ];
      break;
    case 'conversation.forked':
      next.items = [...state.items, { kind: 'forked', seq: event.seq, fromConversationId: str(p.fromConversationId), title: typeof p.title === 'string' ? p.title : null }];
      break;
    case 'memory.saved':
      next.items = [...state.items, { kind: 'memory', seq: event.seq, memoryId: str(p.memoryId), content: str(p.content) }];
      break;
    case 'conversation.updated':
      next.metaVersion = state.metaVersion + 1;
      break;
    case 'snapshot.created':
      next.snapshots = [...state.snapshots, event.seq];
      break;
    case 'turn.completed': {
      next.running = false;
      next.activeRunId = null;
      next.drafts = {};
      next.reasoningDrafts = {};
      next.retry = null;
      next.compacting = null;
      if (run) {
        next.runs = {
          ...state.runs,
          [runId]: {
            ...run,
            completedAt: event.createdAt,
            reason: str(p.reason) || 'completed',
            errorCode: typeof p.errorCode === 'string' ? p.errorCode : null,
            failure: failureOf(p.failure),
            lastAt: event.createdAt,
          },
        };
      }
      if (p.usage && typeof p.usage === 'object') {
        const usage = p.usage as TurnUsage;
        if (runId) next.turnUsage = { ...state.turnUsage, [runId]: usage };
        // replayed after a reload, each turn's own measure takes the place of the one before (the latest stands);
        // a breakdown this page received live (context.usage) is kept
        if (usage.promptTokens && usage.contextWindow && (!state.context || state.context.categories.length === 0)) {
          next.context = { promptTokens: usage.promptTokens, contextWindow: usage.contextWindow, exact: true, categories: [] };
        }
      }
      const reason = str(p.reason);
      if (reason && reason !== 'completed') {
        next.items = [
          ...state.items,
          {
            kind: 'notice',
            seq: event.seq,
            runId: runId || null,
            code: reason as NoticeCode,
            errorCode: typeof p.errorCode === 'string' ? p.errorCode : null,
            limit: (p.limit as { kind: string; used: number; max: number } | undefined) ?? null,
            quota: quotaOf(p.quota),
            ...(failureOf(p.failure) ? { failure: failureOf(p.failure) } : {}),
          },
        ];
      }
      break;
    }
    default:
      break;
  }
  return next;
}

/** On (re)connect the server replays deltas after the last durable event; drop partial drafts. */
export function resetDrafts(state: ChatState): ChatState {
  return Object.keys(state.drafts).length === 0 && Object.keys(state.reasoningDrafts).length === 0 ? state : { ...state, drafts: {}, reasoningDrafts: {} };
}

/** Cards still waiting for the user, oldest first. */
export function pendingApprovals(state: ChatState): ApprovalView[] {
  return Object.values(state.approvals).filter((a) => a.status === 'pending');
}

/** The answer that "regenerate" replaces: the last assistant message, when nothing runs (D-40). */
export function lastAnswer(state: ChatState): Extract<ChatItem, { kind: 'assistant' }> | null {
  if (state.running) return null;
  const last = [...state.items].reverse().find((item) => item.kind === 'assistant' || item.kind === 'user');
  return last?.kind === 'assistant' ? last : null;
}

/** Messages the conversation can go back to (not inside a summary). */
export function rewindPoints(state: ChatState): Extract<ChatItem, { kind: 'user' }>[] {
  return state.items.filter((item): item is Extract<ChatItem, { kind: 'user' }> => item.kind === 'user' && item.seq > state.compactedThrough);
}

/** The last notice offers "continue" when nothing runs after it (D-01, QW-208). */
export function canContinue(state: ChatState): boolean {
  if (state.running) return false;
  const last = [...state.items].reverse().find((item) => item.kind === 'notice' || item.kind === 'user' || item.kind === 'assistant');
  return last?.kind === 'notice' && (last.code === 'limit' || last.code === 'interrupted');
}
